import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../../config";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const SORTABLE_COLUMNS = [
  { key: "id", label: "ID" },
  { key: "batch_no", label: "Batch No" },
  { key: "loa_no", label: "LOA No" },
  { key: "transaction_no", label: "Transaction No" },
  { key: "active", label: "Status" },
  { key: "batched_by", label: "Batched By" },
  { key: "v2_user_id", label: "V2 User ID" },
  { key: "created_at", label: "Created At" },
  { key: "updated_at", label: "Updated At" },
];

const DEFAULT_FILTERS = {
  search: "",
  id: "",
  batch_no: "",
  loa_no: "",
  transaction_no: "",
  v1_user_id: "",
  v2_user_id: "",
  batched_by: "",
  active: "",
  created_from: "",
  created_to: "",
  updated_from: "",
  updated_to: "",
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("authentication/token");
  return { Authorization: `Bearer ${token}` };
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const cleanParams = (input) => {
  const output = {};
  Object.entries(input).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (typeof value === "string" && value.trim() === "") return;
    output[key] = value;
  });
  return output;
};

const ClaimsAlreadyBatchAutditLogsPage = () => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [sortBy, setSortBy] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [selectedIds, setSelectedIds] = useState([]);
  const [busyIds, setBusyIds] = useState([]);

  const [filterOptions, setFilterOptions] = useState({
    users: [],
    batch_nos: [],
    active_options: [],
  });

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/claims/already-batch-autdit-logs/filter-options`,
        {
          headers: getAuthHeaders(),
        }
      );

      setFilterOptions(response.data?.data || {});
    } catch (error) {
      setFilterOptions({
        users: [],
        batch_nos: [],
        active_options: [],
      });
    }
  }, []);

  const fetchRows = useCallback(
    async (targetPage = page, overrideFilters = null) => {
      setLoading(true);
      setErrorMessage("");

      const filtersToUse = overrideFilters || filters;
      const params = cleanParams({
        ...filtersToUse,
        page: targetPage,
        per_page: perPage,
        sort_by: sortBy,
        sort_direction: sortDirection,
      });

      try {
        const response = await axios.get(
          `${BASE_URL}/claims/already-batch-autdit-logs`,
          {
            headers: getAuthHeaders(),
            params,
          }
        );

        const payload = response.data || {};
        const list = payload.data || [];
        const pagination = payload.pagination || {};

        setRows(list);
        setPage(pagination.current_page || targetPage || 1);
        setTotalPages(pagination.total_pages || 1);
        setTotalRecords(pagination.total_records || 0);
      } catch (error) {
        setRows([]);
        setTotalPages(1);
        setTotalRecords(0);
        setErrorMessage(error?.response?.data?.message || "Failed to fetch Claims Already Batch Autdit Logs.");
      } finally {
        setLoading(false);
      }
    },
    [filters, page, perPage, sortBy, sortDirection]
  );

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchRows(1);
    setPage(1);
    setSelectedIds([]);
  }, [sortBy, sortDirection, perPage]);

  const handleApplyFilters = () => {
    fetchRows(1);
    setPage(1);
    setSelectedIds([]);
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
    setSelectedIds([]);
    setSuccessMessage("");
    setErrorMessage("");
    fetchRows(1, DEFAULT_FILTERS);
  };

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(key);
    setSortDirection("asc");
  };

  const sortMarker = (key) => {
    if (sortBy !== key) return "";
    return sortDirection === "asc" ? "ASC" : "DESC";
  };

  const handleChangeFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const toggleSelectAllCurrentPage = () => {
    const currentPageIds = rows.map((row) => row.id);
    const allSelected = currentPageIds.every((id) => selectedSet.has(id));

    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !currentPageIds.includes(id)));
      return;
    }

    const merged = new Set([...selectedIds, ...currentPageIds]);
    setSelectedIds(Array.from(merged));
  };

  const toggleSelectSingle = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((value) => value !== id);
      }
      return [...prev, id];
    });
  };

  const submitSingleStatusUpdate = async (row, nextActive) => {
    setBusyIds((prev) => [...prev, row.id]);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await axios.patch(
        `${BASE_URL}/claims/already-batch-autdit-logs/${row.id}/active`,
        { active: nextActive },
        { headers: getAuthHeaders() }
      );

      setSuccessMessage(
        `Record #${row.id} is now ${nextActive === 1 ? "Active" : "Inactive"}.`
      );
      fetchRows(page);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Failed to update record status."
      );
    } finally {
      setBusyIds((prev) => prev.filter((id) => id !== row.id));
    }
  };

  const submitBulkStatusUpdate = async (nextActive) => {
    if (selectedIds.length === 0) {
      setErrorMessage("Please select at least one record.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await axios.post(
        `${BASE_URL}/claims/already-batch-autdit-logs/bulk-active`,
        {
          ids: selectedIds,
          active: nextActive,
        },
        {
          headers: getAuthHeaders(),
        }
      );

      const updatedCount = response?.data?.updated_count || 0;
      setSuccessMessage(
        `${updatedCount} record(s) set to ${
          nextActive === 1 ? "Active" : "Inactive"
        }.`
      );
      setSelectedIds([]);
      fetchRows(page);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Failed to update selected records."
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedCountOnPage = rows.filter((row) => selectedSet.has(row.id)).length;
  const allCurrentPageSelected =
    rows.length > 0 && selectedCountOnPage === rows.length;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1600px] space-y-5">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 p-6 text-white">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-200">
              Claims Module
            </p>
            <h1 className="mt-1 text-2xl font-semibold">
              Claims Already Batch Autdit Logs
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-200">
              Audit trail of records from <code>v2_claims_is_already_batched</code>{" "}
              with activate/deactivate controls, full filtering, and batched-by
              full name mapping via oauth users.
            </p>
          </div>

          <div className="grid gap-3 border-t border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600 md:grid-cols-4">
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Total Records
              </p>
              <p className="mt-1 text-xl font-semibold text-slate-900">
                {totalRecords.toLocaleString()}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Current Page
              </p>
              <p className="mt-1 text-xl font-semibold text-slate-900">
                {page} / {totalPages}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Selected Records
              </p>
              <p className="mt-1 text-xl font-semibold text-slate-900">
                {selectedIds.length}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Page Size
              </p>
              <p className="mt-1 text-xl font-semibold text-slate-900">{perPage}</p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">
              Filters
            </h2>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleApplyFilters}
                className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-700"
              >
                Apply Filters
              </button>
              <button
                type="button"
                onClick={handleResetFilters}
                className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => fetchRows(page)}
                className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
              >
                Refresh
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <input
              value={filters.search}
              onChange={(event) => handleChangeFilter("search", event.target.value)}
              placeholder="Search any field"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            />
            <input
              value={filters.id}
              onChange={(event) => handleChangeFilter("id", event.target.value)}
              placeholder="ID"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            />
            <select
              value={filters.batch_no}
              onChange={(event) => handleChangeFilter("batch_no", event.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            >
              <option value="">Batch No (All)</option>
              {(filterOptions.batch_nos || []).map((batchNo) => (
                <option key={batchNo} value={batchNo}>
                  {batchNo}
                </option>
              ))}
            </select>
            <input
              value={filters.loa_no}
              onChange={(event) => handleChangeFilter("loa_no", event.target.value)}
              placeholder="LOA No"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            />
            <input
              value={filters.transaction_no}
              onChange={(event) =>
                handleChangeFilter("transaction_no", event.target.value)
              }
              placeholder="Transaction No"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            />
            <input
              value={filters.v1_user_id}
              onChange={(event) =>
                handleChangeFilter("v1_user_id", event.target.value)
              }
              placeholder="V1 User ID"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            />
            <select
              value={filters.v2_user_id}
              onChange={(event) =>
                handleChangeFilter("v2_user_id", event.target.value)
              }
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            >
              <option value="">Batched User (All)</option>
              {(filterOptions.users || []).map((user) => {
                const display =
                  user.full_name && user.full_name.trim() !== ""
                    ? `${user.full_name} (${user.id})`
                    : `${user.username || "No Name"} (${user.id})`;
                return (
                  <option key={user.id} value={user.id}>
                    {display}
                  </option>
                );
              })}
            </select>
            <select
              value={filters.active}
              onChange={(event) => handleChangeFilter("active", event.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            >
              <option value="">Status (All)</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
            <input
              value={filters.batched_by}
              onChange={(event) =>
                handleChangeFilter("batched_by", event.target.value)
              }
              placeholder="Batched By Name"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            />
            <label className="flex flex-col gap-1 text-xs text-slate-500">
              <span>Created From</span>
              <input
                type="date"
                value={filters.created_from}
                onChange={(event) =>
                  handleChangeFilter("created_from", event.target.value)
                }
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 outline-none focus:border-slate-500"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-500">
              <span>Created To</span>
              <input
                type="date"
                value={filters.created_to}
                onChange={(event) =>
                  handleChangeFilter("created_to", event.target.value)
                }
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 outline-none focus:border-slate-500"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-500">
              <span>Updated From</span>
              <input
                type="date"
                value={filters.updated_from}
                onChange={(event) =>
                  handleChangeFilter("updated_from", event.target.value)
                }
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 outline-none focus:border-slate-500"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-500">
              <span>Updated To</span>
              <input
                type="date"
                value={filters.updated_to}
                onChange={(event) =>
                  handleChangeFilter("updated_to", event.target.value)
                }
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 outline-none focus:border-slate-500"
              />
            </label>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => submitBulkStatusUpdate(1)}
                className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
              >
                Bulk Activate
              </button>
              <button
                type="button"
                onClick={() => submitBulkStatusUpdate(0)}
                className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-100"
              >
                Bulk Deactivate
              </button>
              <span className="text-xs text-slate-500">
                {selectedIds.length} selected
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span>Rows per page</span>
              <select
                value={perPage}
                onChange={(event) => setPerPage(Number(event.target.value))}
                className="rounded-md border border-slate-300 px-2 py-1"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {errorMessage && (
            <div className="mx-4 mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {typeof errorMessage === "string"
                ? errorMessage
                : "Something went wrong."}
            </div>
          )}

          {successMessage && (
            <div className="mx-4 mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {successMessage}
            </div>
          )}

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead className="sticky top-0 z-10 bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={allCurrentPageSelected}
                      onChange={toggleSelectAllCurrentPage}
                    />
                  </th>
                  {SORTABLE_COLUMNS.map((column) => (
                    <th key={column.key} className="px-3 py-3 text-left">
                      <button
                        type="button"
                        onClick={() => handleSort(column.key)}
                        className="inline-flex items-center gap-1 font-semibold text-slate-700 hover:text-slate-900"
                      >
                        <span>{column.label}</span>
                        <span className="text-[10px] text-slate-500">
                          {sortMarker(column.key)}
                        </span>
                      </button>
                    </th>
                  ))}
                  <th className="px-3 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={SORTABLE_COLUMNS.length + 2}
                      className="px-4 py-10 text-center text-sm text-slate-500"
                    >
                      Loading logs...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={SORTABLE_COLUMNS.length + 2}
                      className="px-4 py-10 text-center text-sm text-slate-500"
                    >
                      No records found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const busy = busyIds.includes(row.id);
                    const statusActive = Number(row.active) === 1;
                    const batchedBy =
                      row.batched_by_full_name && row.batched_by_full_name.trim()
                        ? row.batched_by_full_name
                        : row.batched_by_username || "-";

                    return (
                      <tr
                        key={row.id}
                        className="border-t border-slate-100 hover:bg-slate-50/70"
                      >
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={selectedSet.has(row.id)}
                            onChange={() => toggleSelectSingle(row.id)}
                          />
                        </td>
                        <td className="px-3 py-3 font-semibold text-slate-900">
                          {row.id}
                        </td>
                        <td className="px-3 py-3 text-slate-700">{row.batch_no || "-"}</td>
                        <td className="px-3 py-3 text-slate-700">{row.loa_no || "-"}</td>
                        <td className="px-3 py-3 font-mono text-xs text-slate-700">
                          {row.transaction_no || "-"}
                        </td>
                        <td className="px-3 py-3">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                              statusActive
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-rose-100 text-rose-800"
                            }`}
                          >
                            {statusActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-slate-700">{batchedBy}</td>
                        <td className="px-3 py-3 text-slate-700">{row.v2_user_id || "-"}</td>
                        <td className="px-3 py-3 text-slate-700">
                          {formatDateTime(row.created_at)}
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          {formatDateTime(row.updated_at)}
                        </td>
                        <td className="px-3 py-3">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              submitSingleStatusUpdate(row, statusActive ? 0 : 1)
                            }
                            className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${
                              statusActive
                                ? "border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"
                                : "border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            } disabled:cursor-not-allowed disabled:opacity-60`}
                          >
                            {busy
                              ? "Please wait..."
                              : statusActive
                              ? "Deactivate"
                              : "Activate"}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 p-4 text-xs text-slate-600">
            <span>
              Showing {rows.length.toLocaleString()} row(s) out of{" "}
              {totalRecords.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const target = Math.max(page - 1, 1);
                  setPage(target);
                  fetchRows(target);
                }}
                disabled={page <= 1 || loading}
                className="rounded-md border border-slate-300 px-3 py-1.5 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Prev
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => {
                  const target = Math.min(page + 1, totalPages);
                  setPage(target);
                  fetchRows(target);
                }}
                disabled={page >= totalPages || loading}
                className="rounded-md border border-slate-300 px-3 py-1.5 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimsAlreadyBatchAutditLogsPage;
